/**
 * Resolves a registry page's Svelte component, one at a time.
 *
 * Split out of `$lib/pages` — and made lazy — because of what the eager version
 * cost. The registry used to `import` all thirty-five components at the top of
 * `index.ts`: five pages across seven locales, every word of marketing copy on
 * the site. `pagePath` lives in that same module and is called by the header and
 * the footer, so the import graph put all thirty-five into the *layout's* chunk
 * — 244 KB, 92 KB over the wire, on every page of the site, to render one of
 * them. A Thai visitor on a product page downloaded the French about page.
 *
 * `import.meta.glob` without `eager` gives a resolver per file instead, so Vite
 * emits each page as its own chunk and only the one being rendered is fetched.
 * This is the same shape as `loadPostBody` and `loadLegalBody`, and it is used
 * from a `+page.ts` for the same reason they are: a universal load can await,
 * and a component cannot travel through server data.
 *
 * The glob pattern must stay a literal — Vite reads it at build time and cannot
 * follow a variable — which is why the mapping below spells out the folder and
 * file stem for each key rather than deriving a path.
 */

import type { Component } from 'svelte';

const MODULES = import.meta.glob<{ default: Component }>('./*/*.svelte');

/**
 * Registry key to the `<folder>/<Stem>` its components are named after.
 *
 * Exported because it is also the only table that knows which file a page's copy
 * lives in, and `$lib/services/content-dates` needs that to report when the page
 * was last revised. Keeping one table means a page cannot be renamed here and go
 * on reporting the old file's date.
 */
export const PAGE_FILES: Record<string, string> = {
	home: 'home/Home',
	about: 'about/About',
	contact: 'contact/Contact',
	faq: 'faq/Faq',
	resellers: 'resellers/Resellers',
	'cannabis-law': 'cannabis-law/CannabisLaw'
};

/**
 * The component for a page in a locale, or `undefined` when there is none.
 *
 * `undefined` rather than a throw, and no cross-locale fallback: the caller
 * renders nothing, which is the behaviour the eager lookup had. A missing
 * translation must not silently serve another language's copy — see the note on
 * fallbacks in `AGENTS.md`.
 */
export async function loadPageBody(key: string, locale: string): Promise<Component | undefined> {
	const stem = PAGE_FILES[key];
	if (!stem) return undefined;

	const resolver = MODULES[`./${stem}.${locale}.svelte`];
	return resolver ? (await resolver()).default : undefined;
}
